import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CheckCircleOutlined, CloseCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import {
  PaymentStatusWrapper,
  StatusIcon,
  StatusTitle,
  StatusMessage,
  ErrorDetails,
  ActionButton,
} from './PaymentStatus.styles';
import { checkPaymentStatus } from '../../../config/api/payment';

// Maps every status NAKA can send (redirect callback, webhook, or charge-status
// polling) to a UI state, so any valid redirect resolves to a sensible screen
// instead of getting stuck on "Processing Payment".
const mapStatusToUi = (rawStatus) => {
  switch (rawStatus) {
    case 'COMPLETED':
    case 'SUCCESS':
    case 'OVERPAID':
      return { uiStatus: 'success', message: 'Your payment was completed successfully.' };
    case 'EXPIRED':
      return { uiStatus: 'error', error: 'This payment link has expired.' };
    case 'CANCELED':
      return { uiStatus: 'error', error: 'The payment was canceled.' };
    case 'UNDERPAID':
      return { uiStatus: 'error', error: 'The amount received was less than expected. Please try again.' };
    case 'FAILED':
      return { uiStatus: 'error', error: 'Payment verification failed.' };
    case 'IN_PROGRESS':
      return { uiStatus: 'processing', message: 'Your payment is still being confirmed...' };
    default:
      return { uiStatus: 'processing', message: 'Processing your payment...' };
  }
};

function PaymentStatus() {
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('Processing your payment...');
  const [error, setError] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const chargeId = params.get('charge_id');
    const urlPaymentStatus = params.get('payment_status');
    const transactionId = params.get('transaction_id');

    // charge_id is the only param guaranteed on every redirect NAKA sends,
    // regardless of outcome (success, expired, canceled, underpaid, ...).
    // transaction_id may be absent when no transaction actually occurred.
    if (!chargeId) {
      setStatus('error');
      setMessage('Invalid payment session');
      return undefined;
    }

    let cancelled = false;
    let attempts = 0;
    const MAX_ATTEMPTS = 10;
    const POLL_INTERVAL_MS = 5000;

    const checkStatus = async () => {
      let resolved;
      let responseMessage;

      try {
        const result = await checkPaymentStatus(transactionId, chargeId, urlPaymentStatus);
        responseMessage = result.message;
        resolved = mapStatusToUi(result.paymentData?.status);
      } catch (err) {
        console.log('Catch ', err);
        // Backend verification itself failed (network/server issue) - fall back
        // to what NAKA told us directly via the redirect rather than a blanket
        // failure, so a genuinely successful payment doesn't look broken.
        resolved = mapStatusToUi(urlPaymentStatus);
        resolved.error = resolved.error || err?.message;
      }

      if (cancelled) return;

      setStatus(resolved.uiStatus);
      setMessage(responseMessage || resolved.message);
      setError(resolved.error || null);

      attempts += 1;
      if (resolved.uiStatus === 'processing') {
        if (attempts < MAX_ATTEMPTS) {
          setTimeout(() => {
            if (!cancelled) checkStatus();
          }, POLL_INTERVAL_MS);
        } else {
          setMessage('Still confirming your payment - please check your application dashboard shortly.');
        }
      }
    };

    checkStatus();

    return () => {
      cancelled = true;
    };
  }, [location.search]);

  const getStatusIcon = () => {
    switch (status) {
      case 'success':
        return <CheckCircleOutlined />;
      case 'error':
        return <CloseCircleOutlined />;
      case 'processing':
        return <LoadingOutlined spin />;
      default:
        return null;
    }
  };

  const handleAction = () => {
    if (status === 'success') {
      navigate('/dashboard');
    } else {
      navigate('/payment');
    }
  };

  const getStatusTitle = () => {
    switch (status) {
      case 'success':
        return 'Payment Successful';
      case 'error':
        return 'Payment Failed';
      default:
        return 'Processing Payment';
    }
  };

  return (
    <PaymentStatusWrapper>
      <StatusIcon status={status}>{getStatusIcon()}</StatusIcon>
      <StatusTitle>{getStatusTitle()}</StatusTitle>
      <StatusMessage>{message}</StatusMessage>

      {error && (
        <ErrorDetails>
          <p>Error Details: {error}</p>
        </ErrorDetails>
      )}

      <ActionButton onClick={handleAction}>{status === 'success' ? 'Go to Dashboard' : 'Try Again'}</ActionButton>
    </PaymentStatusWrapper>
  );
}

export default PaymentStatus;
